import { Component, Injector, OnInit, computed, forwardRef, input, model, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxSelectBoxComponent, DxSelectBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { DxTemplateModule } from 'devextreme-angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DECORATOR_HOST, DecoratorHost } from 'src/app/controls/directives/decorator-host';

@Component({
  selector: 'app-singleselectbox',
  templateUrl: './singleselectbox.component.html',
  styleUrls: ['./singleselectbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleselectboxComponent),
      multi: true
    },
    { 
      provide: DECORATOR_HOST, 
      useExisting: forwardRef(() => SingleselectboxComponent) 
    }
  ],
  host: {
    class: 'link-component'
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DxSelectBoxModule, DxTemplateModule, NgTemplateOutlet]
})
export class SingleselectboxComponent implements OnInit, ControlValueAccessor, DecoratorHost {
  private dx = viewChild.required<DxSelectBoxComponent>('dx');
  private ngControl: NgControl = null;

  value = model<unknown>(null);
  dataSource = input.required<unknown[]>();
  grouped = input<boolean>(false);
  valueExpr = input<string>(null);
  displayExpr = input<string>(null);
  searchExpr = input<string | string[]>(null);
  autoSort = input<boolean>(true);
  itemTemplate = input<unknown>(null);
  groupTemplate = input<unknown>(null);
  readonly = input<boolean>(false);

  private readonly inputId = signal<string | null>(null);
  protected readonly inputAttr = computed<Record<string, string>>(() => this.inputId() ? { id: this.inputId() } : {});
  
  /**
   * @deprecated This input property is deprecated and will be removed in future versions.
   */
  disabled = model<boolean>(false);

  computedDataSource = computed(() => {
    const sortExpr = this.displayExpr() ?? this.valueExpr();
    return this.autoSort()
      ? this.dataSource()?.sort((a, b) => {
          if (sortExpr) {
            const aValue = String(a[sortExpr] ?? '');
            const bValue = String(b[sortExpr] ?? '');
            return aValue.localeCompare(bValue);
          }
          const aStr = String(a ?? '');
          const bStr = String(b ?? '');
          return aStr.localeCompare(bStr);
        })
      : this.dataSource();
  });

  computedSearchExpr = computed(() => {
    const searchValue = this.searchExpr() ?? this.displayExpr();
    return searchValue;
  });

  constructor(
    private injector: Injector
  ) { }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
  }

  get control(): AbstractControl {
    return this.ngControl?.control;
  }

  get isValid(): boolean {
    return !this.control || (this.control.invalid !== true || (this.control.pristine && this.control.untouched));
  }

  onValueChanged(e: ValueChangedEvent): void {
    if (e.value !== this.value()) {
      this.onChange(e.value);
      this.value.set(e.value);
    }
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: unknown): void {
    this.value.set(obj);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }


  /*
   * DecoratorHost interface methods
   */

  setInputId(inputId: string | null): void {
    this.inputId.set(inputId);
  }

  getInputId(): string | null {
    return this.inputId();
  }
}
